/**
 * World-Class Modern Dashboard - Grras Solutions 2025
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../hooks/useCurrency';
import { apiService } from '../services/api';
import SEO from '../components/SEO';
import { RivalsStrip } from '../components/RivalsStrip';
import { DailyMotivationPopup } from '../components/DailyMotivationPopup';
import { COURSE_DATA, ALL_ACCESS } from '../constants/courseData';
import { 
  TrendingUp, 
  BookOpen, 
  Trophy, 
  Target, 
  Clock, 
  Calendar,
  ChevronRight,
  Zap,
  Award,
  BarChart3,
  Activity,
  Users,
  CheckCircle2,
  ArrowUpRight,
  Sparkles,
  Flame,
  Star,
  Brain,
  Rocket
} from 'lucide-react';

// CDN Logo URLs for all courses
const COURSE_LOGOS = {
  RH124: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg', bg: 'bg-red-500' },
  RH134: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg', bg: 'bg-red-600' },
  SVRADM: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg', bg: 'bg-purple-500' },
  ANSIBLE: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg', bg: 'bg-gray-700' },
  DOCKER: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg', bg: 'bg-blue-500' },
  TERRAFORM: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg', bg: 'bg-violet-500' },
  KUBERNETES: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg', bg: 'bg-blue-600' },
  GIT: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg', bg: 'bg-orange-500' },
  PYTHON: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg', bg: 'bg-yellow-500' },
  C_LANG: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg', bg: 'bg-blue-400' },
  CPP: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg', bg: 'bg-indigo-500' },
  MYSQL: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg', bg: 'bg-amber-500' },
  JAVA: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg', bg: 'bg-red-500' },
  EXAM_RHCSA: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg', bg: 'bg-red-700' },
  EXAM_DO188: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg', bg: 'bg-blue-600' },
  EXAM_EX294: { url: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg', bg: 'bg-gray-600' },
};

// Exam courses have different links
const EXAM_LINKS = {
  EXAM_RHCSA: '/exam/rhcsa',
  EXAM_DO188: '/exam/do188',
  EXAM_EX294: '/exam/ex294',
};

const Dashboard = () => {
  const { user } = useAuth();
  const { isIndia } = useCurrency();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [courseAccess, setCourseAccess] = useState({});
  const [upgradePrompts, setUpgradePrompts] = useState({});
  const [pricingInfo, setPricingInfo] = useState(null);
  const [allAccessPricing, setAllAccessPricing] = useState(null);
  // Smart-upsell widget dismissal (session-scoped via sessionStorage)
  const [upsellDismissed, setUpsellDismissed] = useState(() => {
    try { return sessionStorage.getItem('dash_upsell_dismissed') === '1'; } catch { return false; }
  });
  const dismissUpsell = () => {
    try { sessionStorage.setItem('dash_upsell_dismissed', '1'); } catch {}
    setUpsellDismissed(true);
  };
  
  // Format price based on location
  const formatPrice = (courseCode) => {
    const course = COURSE_DATA[courseCode];
    if (!course) return isIndia ? '₹999' : '$45';
    return isIndia 
      ? `₹${course.price.toLocaleString('en-IN')}`
      : `$${course.priceUSD || 19}`;
  };

  useEffect(() => {
    fetchDashboardData();
    
    // Update time every minute
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch core data in parallel
      const [dashData, userPricing, allAccessData] = await Promise.all([
        apiService.getDashboardSummary(),
        apiService.getUserPricingInfo().catch(() => null),
        apiService.getAllAccessPricing().catch(() => null)
      ]);

      setPricingInfo(userPricing);
      setAllAccessPricing(allAccessData);
      
      // Fetch ALL course access data dynamically
      const courseKeys = Object.keys(COURSE_DATA);
      const accessResults = await Promise.all(
        courseKeys.map(code => 
          apiService.getCourseAccess(code).catch(() => ({ user_access: null, upgrade_prompt: null }))
        )
      );
      
      const accessMap = {};
      const promptsMap = {};
      courseKeys.forEach((code, i) => {
        accessMap[code] = accessResults[i];
        promptsMap[code] = accessResults[i]?.upgrade_prompt;
      });
      
      setCourseAccess(accessMap);
      setUpgradePrompts(promptsMap);
      
      // Fetch progress separately with error handling
      let progressData = [];
      try {
        progressData = await apiService.getMyProgress();
      } catch (progressError) {
        // Non-critical - continue without progress data
      }
      
      setSummary({
        ...dashData,
        recent_activity: progressData.slice(0, 5).map((item, index) => ({
          id: index + 1,
          title: item.title,
          status: item.completed_tasks === item.total_tasks ? "completed" : "in-progress",
          xp: item.score,
          progress: item.total_tasks > 0 ? Math.round((item.completed_tasks / item.total_tasks) * 100) : 0,
          course: item.course,
          completed_tasks: item.completed_tasks,
          total_tasks: item.total_tasks
        }))
      });
      
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setSummary({
        total_labs: 0,
        completed_labs: 0,
        total_score: 0,
        max_possible_score: 0,
        recent_activity: []
      });
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 6) return { text: "Working Late", emoji: "🌙", mood: "dedicated" };
    if (hour < 12) return { text: "Good Morning", emoji: "☀️", mood: "energetic" };
    if (hour < 17) return { text: "Good Afternoon", emoji: "🌤️", mood: "productive" };
    if (hour < 21) return { text: "Good Evening", emoji: "🌅", mood: "focused" };
    return { text: "Good Night", emoji: "⭐", mood: "determined" };
  };

  const getMotivationalQuote = () => {
    const quotes = [
      "Excellence is not a skill, it's an attitude. 🚀",
      "The expert in anything was once a beginner. 💪",
      "Success is the sum of small efforts repeated daily. ⚡",
      "Knowledge is power, but implementation is key. 🔥",
      "Every lab completed brings you closer to mastery. 🎯"
    ];
    return quotes[Math.floor(Math.random() * quotes.length)];
  };

  const completionRate = summary?.total_labs > 0
    ? Math.round((summary.completed_labs / summary.total_labs) * 100)
    : 0;

  const streak = {
    current: summary?.streak_days ?? 0,
    longest: summary?.longest_streak ?? 0,
    thisWeek: summary?.active_days_this_week ?? 0,
  };
  const greeting = getGreeting();
  const motivationalQuote = getMotivationalQuote();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto p-6">
          <div className="animate-pulse space-y-8">
            <div className="h-48 bg-gradient-to-r from-gray-200 to-gray-300 rounded-3xl"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`Dashboard - Welcome ${user?.first_name || 'Student'} | DevOps Lab Platform`}
        description="Track your DevOps skills progress across Docker, Kubernetes, Ansible labs. Monitor completion status, learning streaks, and next recommended challenges."
        keywords="DevOps dashboard, container labs progress, Kubernetes training tracker, Ansible automation progress, Docker skills"
      />
      <DailyMotivationPopup />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-testid="dashboard">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-8">
        
        {/* Compact Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 rounded-2xl shadow-xl">
          <div className="absolute inset-0 bg-black/5"></div>
          <div className="absolute -top-2 -right-2 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-white/5 rounded-full blur-2xl"></div>
          
          <div className="relative px-6 sm:px-8 py-4">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-3 lg:space-y-0">
              <div className="flex-1 text-white">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="text-2xl">{greeting.emoji}</div>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold">
                      {greeting.text}, {user?.first_name || 'Champion'}!
                    </h1>
                    <p className="text-blue-100 text-sm">
                      You're feeling {greeting.mood} today
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-3 mb-3">
                  {/* Daily Inspiration - Compact Box */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 flex-1 min-w-0 border border-white/20">
                    <div className="flex items-center space-x-1 mb-1">
                      <Sparkles className="h-3 w-3 text-yellow-300" />
                      <span className="font-medium text-white text-xs">Daily Inspiration</span>
                    </div>
                    <p className="text-white/90 text-xs leading-tight">{motivationalQuote}</p>
                  </div>

                  {/* Ravi Sir's Pro Tips - Compact Box */}
                  <div className="bg-red-500/20 backdrop-blur-sm rounded-lg p-2 flex-1 min-w-0 border border-red-300/30">
                    <div className="flex items-center space-x-1 mb-1">
                      <Brain className="h-3 w-3 text-red-300" />
                      <span className="font-bold text-white text-xs">Ravi Sir's Pro Tips</span>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-white/90 text-xs flex items-start space-x-1">
                        <span className="text-red-300 mt-0.5">•</span>
                        <span>Practice commands daily</span>
                      </p>
                      <p className="text-white/90 text-xs flex items-start space-x-1">
                        <span className="text-red-300 mt-0.5">•</span>
                        <span>Take breaks between sessions</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-sm">
                  <div className="flex items-center space-x-2 bg-white/10 rounded-full px-3 py-1">
                    <Calendar className="h-3 w-3" />
                    <span className="text-xs">{currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white/10 rounded-full px-3 py-1">
                    <Clock className="h-3 w-3" />
                    <span className="text-xs">{currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
              </div>
              
              {/* Compact Progress Circle */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-center">
                  <div className="relative w-16 h-16 mx-auto mb-2">
                    <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 100 100">
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="transparent"
                        className="text-white/20"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="transparent"
                        strokeDasharray={`${2.51 * completionRate} 251.2`}
                        className="text-yellow-300 transition-all duration-1000 ease-out"
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-lg font-bold text-white">{completionRate}%</span>
                    </div>
                  </div>
                  <div className="text-white/90 text-xs font-medium">Overall Progress</div>
                  <div className="text-white/70 text-xs">
                    {summary?.completed_labs || 0}/{summary?.total_labs || 0} labs
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Rivals Strip - Hero motivational banner above stats */}
        <RivalsStrip />

        {/* Enhanced Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Labs */}
          <div className="group relative overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-blue-200/50 hover:-translate-y-2 hover:rotate-1">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-400/20 to-blue-500/20 rounded-full -translate-y-10 translate-x-10"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-900 mb-1" data-testid="total-labs">
                    {summary?.total_labs || 0}
                  </div>
                  <div className="text-xs text-blue-600 bg-blue-200/50 px-2 py-1 rounded-full">
                    Available labs
                  </div>
                </div>
              </div>
              <div className="text-sm font-semibold text-blue-800">Total Labs</div>
              <div className="text-xs text-blue-600 mt-1">Ready for practice</div>
            </div>
          </div>

          {/* Completed Labs */}
          <div className="group relative overflow-hidden bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-green-200/50 hover:-translate-y-2 hover:-rotate-1">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-400/20 to-emerald-500/20 rounded-full -translate-y-10 translate-x-10"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                  <CheckCircle2 className="h-6 w-6 text-white" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-green-900 mb-1" data-testid="completed-labs">
                    {summary?.completed_labs || 0}
                  </div>
                  <div className="flex items-center justify-end text-xs text-green-600 bg-green-200/50 px-2 py-1 rounded-full">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    Great job!
                  </div>
                </div>
              </div>
              <div className="text-sm font-semibold text-green-800">Completed</div>
              <div className="text-xs text-green-600 mt-1">Keep the momentum!</div>
            </div>
          </div>

          {/* XP Points */}
          <div className="group relative overflow-hidden bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-purple-200/50 hover:-translate-y-2 hover:rotate-1">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-400/20 to-purple-500/20 rounded-full -translate-y-10 translate-x-10"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                  <Star className="h-6 w-6 text-white" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-purple-900 mb-1" data-testid="total-score">
                    {summary?.total_score || 0}
                  </div>
                  <div className="text-xs text-purple-600 bg-purple-200/50 px-2 py-1 rounded-full">
                    XP earned
                  </div>
                </div>
              </div>
              <div className="text-sm font-semibold text-purple-800">Experience Points</div>
              <div className="text-xs text-purple-600 mt-1">Level up your skills!</div>
            </div>
          </div>

          {/* Current Streak */}
          <div className="group relative overflow-hidden bg-gradient-to-br from-orange-50 to-red-100 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-orange-200/50 hover:-translate-y-2 hover:-rotate-1">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-400/20 to-red-500/20 rounded-full -translate-y-10 translate-x-10"></div>
            <div className="relative">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-gradient-to-br from-orange-500 to-red-500 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
                  <Flame className="h-6 w-6 text-white" />
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-orange-900 mb-1">
                    {streak.current}
                  </div>
                  <div className="flex items-center justify-end text-xs text-orange-600 bg-orange-200/50 px-2 py-1 rounded-full">
                    <Flame className="h-3 w-3 mr-1" />
                    On fire!
                  </div>
                </div>
              </div>
              <div className="text-sm font-semibold text-orange-800">Day Streak</div>
              <div className="text-xs text-orange-600 mt-1">You're unstoppable!</div>
            </div>
          </div>
        </div>

        {/* Streak Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center justify-between bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl px-6 py-4 shadow border border-orange-200/50">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-orange-500 to-red-500 p-2 rounded-xl shadow">
                <Flame className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-xs text-orange-600 font-semibold uppercase tracking-wide">Current Streak</div>
                <div className="text-2xl font-bold text-orange-700">{streak.current} <span className="text-sm font-normal text-orange-500">days</span></div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl px-6 py-4 shadow border border-purple-200/50">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-purple-500 to-violet-600 p-2 rounded-xl shadow">
                <Trophy className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-xs text-purple-600 font-semibold uppercase tracking-wide">Longest Streak</div>
                <div className="text-2xl font-bold text-purple-700">{streak.longest} <span className="text-sm font-normal text-purple-500">days</span></div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl px-6 py-4 shadow border border-blue-200/50">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2 rounded-xl shadow">
                <Calendar className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Active This Week</div>
                <div className="text-2xl font-bold text-blue-700">{streak.thisWeek} <span className="text-sm font-normal text-blue-500">days</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* Enhanced Quick Actions */}
          <div className="lg:col-span-4 space-y-6">
            <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-6 shadow-xl border border-gray-200">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-400/10 to-purple-500/10 rounded-full -translate-y-16 translate-x-16"></div>
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center relative">
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-2 rounded-xl mr-3 shadow-lg">
                  <Zap className="h-5 w-5 text-white" />
                </div>
                Quick Actions
                <div className="ml-auto text-xs bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full font-medium">
                  Let's Go!
                </div>
              </h2>
              
              <div className="space-y-4 relative">
                <Link 
                  to="/courses"
                  className="group block relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl p-5 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
                  data-testid="browse-courses-button"
                >
                  <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-10 translate-x-10"></div>
                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="bg-white/20 p-3 rounded-xl mr-4 group-hover:scale-110 transition-transform backdrop-blur-sm">
                        <BookOpen className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="font-bold text-white text-lg">Browse Courses</div>
                        <div className="text-blue-100 text-sm">RH124, RH134 & SVRADM Available</div>
                        <div className="text-xs text-blue-200 mt-1">🚀 Start your Linux journey</div>
                      </div>
                    </div>
                    <div className="bg-white/20 p-2 rounded-full group-hover:translate-x-1 transition-transform">
                      <ChevronRight className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </Link>

                <Link
                  to="/mock-tests"
                  className="group block relative overflow-hidden bg-gradient-to-br from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl p-5 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
                >
                  <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-10 translate-x-10"></div>
                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="bg-white/20 p-3 rounded-xl mr-4 group-hover:scale-110 transition-transform backdrop-blur-sm">
                        <Target className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="font-bold text-white text-lg">Mock Tests</div>
                        <div className="text-emerald-100 text-sm">5 Comprehensive Practice Tests</div>
                        <div className="text-xs text-emerald-200 mt-1">🎯 Test your knowledge</div>
                      </div>
                    </div>
                    <div className="bg-white/20 p-2 rounded-full group-hover:translate-x-1 transition-transform">
                      <ChevronRight className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </Link>

                {/* RHCSA Exam Practice - Premium Feature */}
                <Link
                  to="/exam/rhcsa"
                  className="group block relative overflow-hidden bg-gradient-to-br from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl p-5 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
                  data-testid="rhcsa-exam-card"
                >
                  <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-10 translate-x-10"></div>
                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="bg-white/20 p-3 rounded-xl mr-4 group-hover:scale-110 transition-transform backdrop-blur-sm">
                        <Award className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="font-bold text-white text-lg flex items-center gap-2">
                          RHCSA Exam Practice
                          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">NEW</span>
                        </div>
                        <div className="text-red-100 text-sm">3 Complete Practice Exam Sets</div>
                        <div className="text-xs text-red-200 mt-1">🎯 Real VM-based • 45 Questions</div>
                      </div>
                    </div>
                    <div className="bg-white/20 p-2 rounded-full group-hover:translate-x-1 transition-transform">
                      <ChevronRight className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>

            {/* Course Progress & Upgrade Prompts (Freemium Model) */}
            <div className="relative overflow-hidden bg-gradient-to-br from-indigo-50 to-blue-100 rounded-2xl p-6 shadow-xl border border-indigo-200">
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-indigo-400/20 to-blue-500/20 rounded-full -translate-y-12 translate-x-12"></div>
              <h3 className="font-bold text-gray-900 mb-6 flex items-center relative">
                <div className="bg-gradient-to-br from-indigo-500 to-blue-600 p-2 rounded-xl mr-3 shadow-lg">
                  <BookOpen className="h-5 w-5 text-white" />
                </div>
                Your Learning Path
                <div className="ml-auto text-xs bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full font-medium">
                  All Courses
                </div>
              </h3>

              <div className="space-y-4 relative">
                
                {/* Smart All Access Upsell Widget (conditional) */}
                {(() => {
                  // CANONICAL CHECK: use pricingInfo.has_all_access (single source of truth
                  // from /api/payments/user-pricing-info). Earlier this checked
                  // courseAccess['ALL_ACCESS']?.user_access?.access_type which silently
                  // returned undefined (ALL_ACCESS isn't even in COURSE_DATA), causing the
                  // widget to show to All Access users — May 2026 user report.
                  const hasAllAccess = Boolean(pricingInfo?.has_all_access);
                  const credit = allAccessPricing?.credit || 0;
                  const finalPrice = allAccessPricing?.final_price || 0;
                  const remainingCourses = allAccessPricing?.remaining_courses_only || 0;
                  const remainingExams = allAccessPricing?.remaining_exams_only || 0;
                  const remainingTotal = remainingCourses + remainingExams;
                  // Smart conditions: hide if dismissed, if already has All Access, if no investment yet, or no upgrade benefit
                  const shouldShow = !upsellDismissed && !hasAllAccess && credit > 0 && finalPrice > 0 && remainingTotal > 0 && isIndia;
                  if (!shouldShow) return null;
                  return (
                    <div
                      data-testid="dashboard-upsell-widget"
                      className="relative flex items-center gap-3 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 border border-amber-200 rounded-xl px-4 py-3 shadow-sm"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-slate-800">
                          You've invested <span className="text-orange-600">₹{credit.toLocaleString()}</span> · Level up to All Access for just <span className="text-orange-600">₹{finalPrice.toLocaleString()}</span> more
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Unlock {remainingCourses > 0 && (<>{remainingCourses} more {remainingCourses === 1 ? 'course' : 'courses'}</>)}
                          {remainingCourses > 0 && remainingExams > 0 && ' + '}
                          {remainingExams > 0 && (<>{remainingExams} exam {remainingExams === 1 ? 'attempt' : 'attempts'}</>)}
                          {' '}FREE
                        </div>
                      </div>
                      <Link
                        to="/payment/ALL_ACCESS"
                        data-testid="dashboard-upsell-cta"
                        className="flex-shrink-0 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold px-3 py-2 rounded-lg transition whitespace-nowrap"
                      >
                        Upgrade ₹{finalPrice.toLocaleString()}
                      </Link>
                      <button
                        type="button"
                        onClick={dismissUpsell}
                        data-testid="dashboard-upsell-dismiss"
                        aria-label="Dismiss"
                        className="flex-shrink-0 w-6 h-6 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-600 flex items-center justify-center text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })()}

                {/* Quick Navigation Card */}
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-4 text-white">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="flex items-center space-x-3">
                      <Rocket className="h-6 w-6" />
                      <div>
                        <h4 className="font-bold">Explore All Courses & Bundles</h4>
                        <p className="text-indigo-100 text-sm">View pricing, upgrades & certification paths</p>
                      </div>
                    </div>
                    <Link 
                      to="/pricing"
                      className="bg-white text-indigo-600 px-5 py-2 rounded-lg font-semibold text-sm hover:bg-indigo-50 transition-all min-h-[40px] touch-manipulation"
                    >
                      View Pricing →
                    </Link>
                  </div>
                </div>

                {/* Individual Course Progress - Dynamic */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                  <h5 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <BookOpen className="h-4 w-4 mr-2 text-blue-600" />
                    Individual Course Progress
                    <span className="ml-auto text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full">
                      {(() => {
                        const hasAA = Boolean(pricingInfo?.has_all_access);
                        const total = Object.keys(COURSE_DATA).length;
                        if (hasAA) {
                          // All Access covers every bundle SKU — count all as enrolled
                          const aaCovered = Object.keys(COURSE_DATA).filter(c => c !== 'MOCK_TESTS' && c !== 'PROJECTS_ACCESS').length;
                          return `${aaCovered}/${total} enrolled`;
                        }
                        return `${Object.values(courseAccess).filter(a => a?.user_access?.access_type === 'premium').length}/${total} enrolled`;
                      })()}
                    </span>
                  </h5>
                  <div className="space-y-3">
                
                {/* Render ALL courses dynamically - enrolled first, then rest */}
                {(() => {
                  const courseKeys = Object.keys(COURSE_DATA);
                  const hasAA = Boolean(pricingInfo?.has_all_access);
                  // Helper: treat course as "premium" if user has individual access OR
                  // is covered by All Access (bundle SKU only — Mock Tests / Projects excluded).
                  const isPremium = (code) => {
                    if (hasAA && code !== 'MOCK_TESTS' && code !== 'PROJECTS_ACCESS') return true;
                    return courseAccess[code]?.user_access?.access_type === 'premium';
                  };
                  
                  // Sort: enrolled (premium) first, then with progress, then rest
                  const sorted = [...courseKeys].sort((a, b) => {
                    const aAccess = courseAccess[a]?.user_access;
                    const bAccess = courseAccess[b]?.user_access;
                    const aScore = isPremium(a) ? 2 : (aAccess?.completed_labs > 0 ? 1 : 0);
                    const bScore = isPremium(b) ? 2 : (bAccess?.completed_labs > 0 ? 1 : 0);
                    return bScore - aScore;
                  });

                  const enrolledCourses = sorted.filter(isPremium);
                  const notEnrolledCourses = sorted.filter(code => !isPremium(code));

                  return (
                    <>
                      {/* Enrolled Courses - Full cards with progress */}
                      {enrolledCourses.map(code => {
                        const course = COURSE_DATA[code];
                        const access = courseAccess[code];
                        const userAccess = access?.user_access;
                        const logoConfig = COURSE_LOGOS[code] || { url: '', bg: 'bg-gray-500' };
                        const labLink = EXAM_LINKS[code] || `/courses/${code}`;
                        const progressPct = userAccess?.progress_percentage || 0;

                        return (
                          <div 
                            key={code} 
                            className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-emerald-200 transition-all duration-300 hover:shadow-lg"
                            data-testid={`dashboard-course-${code}`}
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <div className={`${logoConfig.bg} p-2 rounded-lg flex-shrink-0`}>
                                <img src={logoConfig.url} alt={code} className="w-5 h-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-gray-900 text-sm truncate">{course?.fullName || code}</h4>
                                <span className="text-xs text-gray-500">
                                  {userAccess?.completed_labs || 0} of {userAccess?.total_labs || course?.labs || 0} labs done
                                </span>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <span className="text-sm font-bold text-emerald-600">{progressPct}%</span>
                                <div className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium mt-0.5">
                                  Premium
                                </div>
                              </div>
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="bg-gray-200 rounded-full h-2 mb-3 overflow-hidden">
                              <div 
                                className="h-full bg-emerald-500 transition-all duration-500 rounded-full"
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                            
                            <Link
                              to={labLink}
                              className="flex items-center justify-center w-full bg-emerald-500 hover:bg-emerald-600 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                              data-testid={`dashboard-view-${code}`}
                            >
                              {course?.isExamCourse ? 'Start Exam Practice' : 'Continue Learning'} <ChevronRight className="ml-1 h-4 w-4" />
                            </Link>
                          </div>
                        );
                      })}

                      {enrolledCourses.length === 0 && (
                        <div className="bg-white/60 rounded-xl p-4 text-center border border-dashed border-blue-200">
                          <BookOpen className="h-8 w-8 text-blue-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-600 mb-2">No courses enrolled yet</p>
                          <Link to="/courses" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                            Browse Courses →
                          </Link>
                        </div>
                      )}

                      {/* Not Enrolled - Just a summary link, not individual cards */}
                      {notEnrolledCourses.length > 0 && (
                        <Link 
                          to="/courses"
                          className="block bg-white/60 rounded-lg p-3 border border-dashed border-indigo-200 text-center hover:border-indigo-400 hover:bg-indigo-50/50 transition-all"
                        >
                          <p className="text-sm font-medium text-indigo-600">
                            Explore {notEnrolledCourses.length} more courses →
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">Linux, DevOps, Programming, Database & Exams</p>
                        </Link>
                      )}
                    </>
                  );
                })()}
                
                  </div> {/* Close Individual Course Progress section */}
                </div>
              </div>
            </div>

          </div>

          {/* Enhanced Learning Activity */}
          <div className="lg:col-span-8">
            <div className="relative overflow-hidden bg-gradient-to-br from-violet-50 to-indigo-100 rounded-2xl p-6 shadow-xl border border-violet-200">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-violet-400/10 to-indigo-500/10 rounded-full -translate-y-20 translate-x-20"></div>
              
              <div className="relative flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <div className="bg-gradient-to-br from-violet-500 to-indigo-600 p-2 rounded-xl mr-3 shadow-lg">
                    <Activity className="h-5 w-5 text-white" />
                  </div>
                  Recent Learning Activity
                  <div className="ml-3 px-2 py-1 bg-violet-100 text-violet-700 text-xs rounded-full font-medium">
                    Live
                  </div>
                </h2>
                <div className="flex items-center space-x-2">
                  <div className="text-sm text-gray-600 bg-white/60 backdrop-blur-sm px-3 py-1 rounded-full border border-white/40">
                    {summary?.recent_activity?.length || 0} active labs
                  </div>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                </div>
              </div>

              <div className="space-y-4 relative" data-testid="recent-activity">
                {summary?.recent_activity?.length > 0 ? (
                  summary.recent_activity.map((activity, index) => (
                    <div key={activity.id} className="group relative overflow-hidden bg-white/70 backdrop-blur-sm hover:bg-white/90 rounded-xl p-5 transition-all duration-300 border border-white/50 hover:border-violet-200 hover:shadow-lg transform hover:-translate-y-1">
                      <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-violet-400/10 to-indigo-500/10 rounded-full -translate-y-8 translate-x-8"></div>
                      
                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <div className={`relative w-4 h-4 rounded-full ${
                            activity.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'
                          } shadow-lg`}>
                            <div className={`absolute inset-0 rounded-full ${
                              activity.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'
                            } animate-ping opacity-75`}></div>
                          </div>
                          
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900 mb-2 text-lg">{activity.title}</div>
                            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                              <span className="bg-gradient-to-r from-violet-500 to-indigo-600 text-white px-3 py-1 rounded-lg text-xs font-semibold shadow-md">
                                {activity.course}
                              </span>
                              <span className="flex items-center bg-gray-100 px-2 py-1 rounded-md">
                                <Users className="h-3 w-3 mr-1 text-gray-500" />
                                {activity.completed_tasks}/{activity.total_tasks} tasks
                              </span>
                            </div>
                            
                            {/* Enhanced Progress Bar */}
                            <div className="flex items-center space-x-3">
                              <div className="flex-1 bg-gray-200 rounded-full h-3 shadow-inner">
                                <div 
                                  className={`h-3 rounded-full transition-all duration-700 shadow-sm ${
                                    activity.status === 'completed' 
                                      ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                                      : 'bg-gradient-to-r from-blue-500 to-indigo-600'
                                  }`}
                                  style={{width: `${activity.progress}%`}}
                                >
                                  <div className="h-full w-full bg-white/30 rounded-full"></div>
                                </div>
                              </div>
                              <span className="text-sm font-bold text-gray-700 min-w-[3rem]">{activity.progress}%</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right ml-6">
                          <div className="flex items-center justify-end text-base font-bold text-amber-600 mb-2">
                            <Award className="h-5 w-5 mr-1" />
                            +{activity.xp} XP
                          </div>
                          <div className={`inline-flex items-center text-sm px-4 py-2 rounded-full font-semibold shadow-md ${
                            activity.status === 'completed' 
                              ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' 
                              : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
                          }`}>
                            {activity.status === 'completed' ? '✅ Completed' : '⚡ In Progress'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="relative text-center py-20 bg-white/50 backdrop-blur-sm rounded-xl border border-white/40">
                    <div className="text-8xl mb-6">🚀</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Ready to Launch Your Journey?</h3>
                    <p className="text-gray-600 mb-8 max-w-lg mx-auto text-lg leading-relaxed">
                      Begin your DevOps certification adventure with expert-designed hands-on labs and real-world scenarios.
                    </p>
                    <Link
                      to="/courses"
                      className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-xl font-bold text-lg transition-all duration-300 shadow-2xl hover:shadow-violet-500/25 transform hover:-translate-y-2"
                    >
                      <Rocket className="h-6 w-6 mr-3" />
                      Launch Your First Lab
                      <ArrowUpRight className="h-5 w-5 ml-3" />
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Compact Certification Journey */}
        <div className="bg-gradient-to-r from-gray-800 via-gray-900 to-black rounded-2xl shadow-2xl overflow-hidden">
          <div className="relative p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center bg-gradient-to-r from-yellow-500 to-amber-600 text-black px-4 py-2 rounded-full text-sm font-bold mb-3">
                <Trophy className="h-4 w-4 mr-2" />
                GRRAS Learning Labs
              </div>
              
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Your DevOps Mastery Journey
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-sm">
                Every lab brings you closer to industry certification excellence
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-4 border border-blue-500/30">
                <div className="text-center">
                  <div className="text-2xl mb-2">📚</div>
                  <div className="text-xl font-bold text-white mb-1">{summary?.total_labs || 0}</div>
                  <div className="text-gray-300 text-sm font-medium">Total Labs</div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-500/20 to-emerald-600/20 backdrop-blur-sm rounded-xl p-4 border border-green-500/30">
                <div className="text-center">
                  <div className="text-2xl mb-2">✅</div>
                  <div className="text-xl font-bold text-white mb-1">{summary?.completed_labs || 0}</div>
                  <div className="text-gray-300 text-sm font-medium">Completed</div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-amber-500/20 to-yellow-600/20 backdrop-blur-sm rounded-xl p-4 border border-amber-500/30">
                <div className="text-center">
                  <div className="text-2xl mb-2">🏆</div>
                  <div className="text-xl font-bold text-white mb-1">{completionRate}%</div>
                  <div className="text-gray-300 text-sm font-medium">Progress</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Dashboard;
